<script setup lang="ts">
  // External library imports
  import type { PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import CPDivider from '@ui/components/CPDivider.vue'

  import CPText from '@ui/components/CPText.vue'

  // Internal imports
  import { createElementId } from '../../../core-layer/utils'

  import { useAuth } from '../../composables/useAuth'

  import { type GoogleCredentialResponse, LoginEvents, type LoginCallbackPayload, type ScreenChangeData } from '../../types'

  import { getConfig } from '../../../core-layer'

  // Type imports

  // Props definitions
  const props = defineProps({
    loginType: {
      type: String,
      required: true,
    },
    referrerUser: {
      type: Object as PropType<{
        email: string
        name: string
        _id?: string
      }>,
      default: null,
      required: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    (e: 'changeScreen', value: ScreenChangeData): void
    (e: 'loginCallback', value: LoginCallbackPayload): void
    (e: 'updateLoader', value: boolean): void
  }>()

  // Composables
  const { t } = useI18n()

  // Refs and reactive variables
  const isLoading = ref(true)

  // Lifecycle hooks
  onMounted(() => {
    // Wait for Google script to be available (loaded in nuxt.config.ts head)
    let attempts = 0
    const maxAttempts = 50 // 5 seconds max wait time

    const checkGoogle = () => {
      attempts++

      if (window.google?.accounts) {
        initializeGoogleSSO()
      } else if (attempts < maxAttempts) {
        // Retry after a short delay
        setTimeout(checkGoogle, 100)
      } else {
        // Timeout - hide loading and show error
        isLoading.value = false
        emit('updateLoader', false)
        loadGoogleScriptManually()
      }
    }
    checkGoogle()
  })

  // Declare google global (Google Identity Services / GSI client)
  declare global {
    interface Window {
      google?: {
        accounts?: {
          id?: {
            initialize: (config: {
              client_id: string
              callback: (response: GoogleCredentialResponse) => void
              auto_select?: boolean
              use_fedcm_for_prompt?: boolean
              state_cookie_domain?: string
            }) => void
            renderButton: (element: HTMLElement, options: Record<string, string | number>) => void
          }
        }
      }
    }
  }

  // Fallback function to load Google script manually
  function loadGoogleScriptManually() {
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      setTimeout(() => {
        if (window.google?.accounts) {
          initializeGoogleSSO()
        }
      }, 100)
    }
    script.onerror = () => {
      console.error('Failed to load Google script manually')
    }
    document.head.appendChild(script)
  }

  // User-defined functions
  /**
   * Usage:
   * After user is logged in using SSO, remove the referral code from local storage immediately
   * to avoid any future use of it just in case the login callback event is not emitted
   */
  function updateRefLocalStorage() {
    // window.localStorage.removeItem('refCode')
  }

  function initializeGoogleSSO() {
    emit('updateLoader', true)

    // Get the current origin for cookie domain
    const currentOrigin = window.location.origin

    try {
      window.google?.accounts?.id?.initialize({
        client_id: getConfig()?.KOLLAB_GOOGLE_CLIENT_ID || '',
        callback: handleCredentialResponse,
        auto_select: true, // Automatically prompt users who are previously signed in
        use_fedcm_for_prompt: true,
        state_cookie_domain: currentOrigin,
      })

      // Hide loading state FIRST so the buttonDiv becomes visible
      isLoading.value = false
      emit('updateLoader', false)

      // Wait for DOM to update before trying to render the button
      setTimeout(() => {
        const buttonDiv = document.getElementById('buttonDiv')

        if (buttonDiv) {
          window.google?.accounts?.id?.renderButton(buttonDiv, {
            theme: 'outline',
            logo_alignment: 'center',
            size: 'medium',
            width: 450,
            shape: 'rectangular',
            state_cookie_domain: currentOrigin,
            text: 'continue_with',
            type: 'standard',
          })
        } else {
          console.error('Button div not found!')
        }
      }, 100) // Small delay to ensure DOM is updated
    } catch (error) {
      console.error('Error initializing Google SSO:', error)
      isLoading.value = false
      emit('updateLoader', false)
    }

    // window.google.accounts.id.prompt() // Trigger the Google One Tap directly
  }

  async function handleCredentialResponse(response: GoogleCredentialResponse) {
    if (!response.credential) return

    emit('updateLoader', true)
    try {
      const { data } = await useAuth().loginWithGoogle(response.credential)

      if (data?.token) {
        if (props.referrerUser?._id) {
          updateRefLocalStorage()
        }
        emit(LoginEvents.LOGIN_CALLBACK, {
          eventType: LoginEvents.LOGIN_SUCCESSFUL,
          ...data,
        })
      }
    } catch (error) {
      console.error('Error handling credential response:', error)
    } finally {
      emit('updateLoader', false)
    }
  }
</script>

<template>
  <div>
    <div class="flex w-full items-center justify-center">
      <div v-if="isLoading" id="google-sso-loading" class="google-button custom-google-button flex w-full items-center justify-center">
        <div class="h-4 w-4 animate-spin rounded-full border-2 border-default border-t-primary"></div>
        <span class="ml-2 text-sm text-600">{{ t('auth.loadingGoogleSignIn') }}</span>
      </div>
      <div v-else id="buttonDiv" class="google-button custom-google-button w-full"></div>
    </div>

    <CPDivider class="my-4">
      <CPText :id="createElementId('login-modal', 'with-email', 'divider')" class="text-secondary" size="xs" weight="medium">
        {{ loginType === 'SIGN_IN' ? t('auth.orLoginWithYourEmail') : t('auth.orSignUpWithYourEmail') }}
      </CPText>
    </CPDivider>
  </div>
</template>

<style scoped>
  .hr-divider:not(.hr-divider--vertical) {
    margin: 12px 0;
  }

  .custom-google-button {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 44px;
  }

  .google-button {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  /* Target the iframe itself */
  .custom-google-button iframe {
    height: 44px !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .form-item {
    height: 62px !important;
  }

  /* Google Sign-In button full width styles */
  #buttonDiv {
    width: 100%;
  }

  #buttonDiv :deep(> div) {
    width: 100% !important;
  }

  #buttonDiv :deep(> div > div) {
    width: 100% !important;
  }

  #buttonDiv :deep([role='button']) {
    width: 100% !important;
    max-width: 100% !important;
    min-width: unset !important;
  }

  #buttonDiv :deep([role='button'] > div:first-child) {
    width: 100% !important;
  }
</style>
